/**
 * Per-column filters for the requirements LIST view (Akhil, 2026-09-15).
 *
 * The filter bar above the board narrows on a few fields; a list of hundreds wants the filter
 * where the eye already is — under each column heading. Text columns match a substring; the
 * three choice columns (status, type, purchaser) match exactly, from the values actually on
 * screen so no choice can produce an empty table for a value nobody has.
 *
 * Applied AFTER the filter bar, on the same rows — the two compose with AND.
 */

import type { EnquirySummary } from './types'

export interface ColumnFilters {
  enquiry: string
  customer: string
  vessel: string
  enquiry_type: string
  status: string
  quotation: string
  purchaser: string
}

export const EMPTY_COLUMN_FILTERS: ColumnFilters = {
  enquiry: '',
  customer: '',
  vessel: '',
  enquiry_type: '',
  status: '',
  quotation: '',
  purchaser: '',
}

const norm = (v: unknown) => String(v ?? '').trim().toLowerCase()
const has = (value: unknown, needle: string) => needle === '' || norm(value).includes(needle)

export function applyColumnFilters(rows: readonly EnquirySummary[], f: ColumnFilters): EnquirySummary[] {
  const enquiry = norm(f.enquiry)
  const customer = norm(f.customer)
  const vessel = norm(f.vessel)
  const quotation = norm(f.quotation)
  return rows.filter(
    (r) =>
      (has(r.id, enquiry) || has(r.ref, enquiry)) &&
      has(r.customer_name, customer) &&
      has(r.vessel, vessel) &&
      has(r.quotation_id, quotation) &&
      (f.status === '' || r.status === f.status) &&
      (f.enquiry_type === '' || r.enquiry_type === f.enquiry_type) &&
      (f.purchaser === '' || r.purchaser === f.purchaser),
  )
}

/** The choices the three select columns offer — what is on screen, sorted. */
export function columnOptions(rows: readonly EnquirySummary[]): { status: string[]; enquiry_type: string[]; purchaser: string[] } {
  const uniq = (values: string[]) => [...new Set(values.filter((v) => v !== ''))].sort()
  return {
    status: uniq(rows.map((r) => r.status)),
    enquiry_type: uniq(rows.map((r) => r.enquiry_type)),
    purchaser: uniq(rows.map((r) => r.purchaser)),
  }
}

export const activeColumnFilterCount = (f: ColumnFilters): number => Object.values(f).filter((v) => v !== '').length
